import { z } from "zod";

import { userSchema } from "../../user/userSchema";

export enum RoleID {
  Student = 1,
  Teacher = 2,
}
export const USERNAME_MIN_CHARS = 3;
export const USERNAME_MAX_CHARS = 15;

export const adminDashUserSchema = userSchema.extend({
  email: z.email().nullable(),
  role_id: z.enum(RoleID).optional().nullable(),
  subscription_end_date: z.string().optional().nullable(),
  parent_email: z.email().nullable().optional(),
});

export const adminDashMembersSchema = z.object({
  all_user_ids: z.number().array(),
  non_admin_user_ids: z.number().array(),
  results: adminDashUserSchema.array(),
  total: z.number(),
});

/*
 * Originally these schemas extended the adminDashUserSchema, but form validation required too many
 * minute changes to the fields (minimum lengths, everything has to be a string), and trying to extend
 * the original schema with all those changes resulted in some pretty bizarre field orders in the relevant forms.
 */
export const schoolMemberForm = z.object({
  name: z.string().min(1),
  last_name: z.string().min(1),
  email: z.email(),
  role_id: z
    .literal(RoleID.Teacher.toString())
    .or(z.literal(RoleID.Student.toString())),
});
export const schoolMemberImportSchema = z
  .object({
    name: z.string().min(1),
    last_name: z.string().min(1),
    parent_email: z
      .union([
        z.email({ error: "web_admin_dash_parent_email_invalid" }),
        z.literal(""),
      ])
      .optional(),
  })
  .and(
    z.union([
      z.object({
        email: z.email(),
        username: z.never().optional(),
      }),
      z.object({
        email: z.never().optional(),
        username: z
          .string()
          .superRefine((val, ctx) => {
            if (val) {
              if (
                val.length < USERNAME_MIN_CHARS ||
                val.length > USERNAME_MAX_CHARS
              ) {
                ctx.addIssue({
                  code: "custom",
                  message: "web_username_validation_error_invalid_length",
                });
              }
              // the error message doesn't mention Unicode letters, but they
              // are present in existing production usernames, so best not to
              // error out on them
              if (/[^a-zA-Z0-9.\p{L}]/gu.test(val)) {
                ctx.addIssue({
                  code: "custom",
                  message: "web_admin_dash_username_validation",
                });
              }
            }
          })
          .optional(),
      }),
    ]),
  );
/**
 * At the moment, this schema is hypothetical and used for display more than anything else. "Regular"
 * users should not be editable in the current state of the admin dashboard.
 */
export const regularMemberForm = z.object({
  name: z.string().min(1),
  last_name: z.string().min(1),
  username: z.string().min(1),
  bio: z.string().optional(),
});

export const adminDashImportStatusSchema = z.object({
  percent_complete: z.number(),
  num_complete: z.number(),
  num_total: z.number(),
  errored_emails: z.string().array().optional().nullable(),
});

export type AdminDashUser = z.infer<typeof adminDashUserSchema>;
export type SchoolMember = z.infer<typeof schoolMemberForm>;
export type SchoolMemberImport = z.infer<typeof schoolMemberImportSchema>;
export type RegularMember = z.infer<typeof regularMemberForm>;
export type AdminDashMembers = z.infer<typeof adminDashMembersSchema>;
